import ScreenHeader from 'common/components/screenHeader/ScreenHeader';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScreenTitle from '../../common/components/screenTitle/ScreenTitle';
import ScreenContainer from '../../common/components/screenContainer/ScreenContainer';
import Box from 'common/components/box/Box';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import styles from './VisitsReportScreen.module.scss';
import { DATE_TIME_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import { useHistory } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import Popover from 'common/components/popover/Popover';
import VisitsReportFiltersScreen, { Filters } from './components/VisitsReportFiltersScreen';
import DateFormat from 'common/components/dateFormat/dateFormat';
import FilterList from 'common/components/filterList/FilterList';
import ReactTooltip from 'react-tooltip';
import Loading from 'common/services/Loading';

import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { StoppingAreaItemsSearchCriteria } from 'api/stoppingAreaItems/models/StoppingAreaItemsSearchCriteria';
import { StoppingAreaItemsListItemDto } from 'api/stoppingAreaItems/models/StoppingAreasListItemDto';
import StoppingAreaItemsService from 'api/stoppingAreaItems/StoppingAreaItemsService';
import CounterBox from 'common/components/counterBox/CounterBox';
import CarIcon from 'assets/svg/car.svg';
import HourglassIcon from 'assets/svg/hour-glass_white.svg';
import PointIcon from 'assets/svg/desktop_GPS_white.svg';

import { StoppingAreasItemConterDto } from 'api/stoppingAreaItems/models/StoppingAreasItemConterDto';
import { FaInfoCircle } from 'react-icons/fa';

const VisitsReportScreen = () => {

    const { t } = useTranslation();
    const { addToast } = useToasts();
    const [criteria, setCriteria] = useState<StoppingAreaItemsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'entry_date',
        orderBy: 'desc',
    });
    const guidEmpty = '00000000-0000-0000-0000-000000000000';
    const [stoppingAreaItems, setStoppingAreaItems] = useState<StoppingAreaItemsListItemDto[]>([]);
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [counters, setCounters] = useState<StoppingAreasItemConterDto>();

    const getData = async () => {

        try {
            Loading.show();
            const [page, count] = await Promise.all([
                StoppingAreaItemsService.getList(criteria),
                StoppingAreaItemsService.getCounters(criteria)
            ]);
            setCurrentPage(page.currentPage);
            setTotalItems(page.totalItems);
            setStoppingAreaItems(page.items);
            setCounters(count);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.STOPPING_AREAS, `Couldn't get stopping area item list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    }

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const renderTableCell = (text?: string | number) => {
        return (
            <div>{text ?? '-'}</div>
        )
    }

       const renderActiveTableCell = (text?: string | number,toolTip?:string) => {
        return (
            <div>{text ?? t('visits_report.list.no_asset_defined')}{!text && toolTip && <span style={{ marginLeft:'5px' }}><FaInfoCircle  data-tip={t('visits_report.list.no_asset_defined_tooltip').replace('{0}',toolTip)} /> <ReactTooltip /></span>}</div>
        )
    }

    const calcDuration = (entryDate: Date, exitDate: Date) => {
        const diffInTime = new Date(exitDate).setSeconds(0) - new Date(entryDate).setSeconds(0);
        const days = Math.floor(diffInTime / (1000 * 60 * 60 * 24))
        const hours = Math.floor(((diffInTime - (days * 1000 * 60 * 60 * 24)) / (1000 * 60 * 60)))
        const minutes = Math.floor((diffInTime - (days * 1000 * 60 * 60 * 24) - (hours * 1000 * 60 * 60)) / (1000 * 60));
        if (days > 0) {
            return `${days.toString()}${t('visits_report.list.day_mask')} - ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}  h`;
        } else {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} h`;
        }

    }


    const tableColumns: ListingTableColumn<StoppingAreaItemsListItemDto>[] = [
        {
            name: t('visits_report.list.stopping_area'),
            renderCell: row => renderTableCell(row.stoppingArea),
            width: 'fill'
        },
        {
            name: t('visits_report.list.vehicle'),
            renderCell: row => renderActiveTableCell(row.registrationNumber, row.code),
            width: 220,
        },
        {
            name: t('visits_report.list.driver'),
            renderCell: row => renderTableCell(row.verificationDriverName),
            width: 150,
        },
        {
            name: t('visits_report.list.duration'),
            renderCell: row => renderTableCell(calcDuration(row.entryDate, row.exitDate)),
            onSearch: onTableFilter,
            searchField: 'minutes',
            width: 200,
            hideOn: ['sm', 'md']
        },
        {
            name: t('visits_report.list.entry'),
            renderCell: row => row.entryDate ? <DateFormat value={row.entryDate} format={DATE_TIME_FORMAT_DEFAUT} /> : <>-</>,
            onSearch: onTableFilter,
            searchField: 'entry_date',
            width: 200,
            hideOn: ['sm', 'md']
        },
        {
            name: t('visits_report.list.exit'),
            renderCell: row => row.exitDate ? <DateFormat value={row.exitDate} format={DATE_TIME_FORMAT_DEFAUT} /> : <>-</>,
            onSearch: onTableFilter,
            searchField: 'exit_date',
            width: 200,
            hideOn: ['sm', 'md']
        }
    ];

    const onRowClick = (event: any, id?: string) => {
     
        if (id && id != guidEmpty) {
            event.preventDefault();
            event.stopPropagation();

            if (event.ctrlKey) {
                window.open('/verifications/details/' + id, '_blank');
                return;
            }
            history.push(`/verifications/details/${id}`)
        }
    }

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(f);
        localStorage.setItem('VISITSREPORT', JSON.stringify(f));
    }

    const onPageChange = (page: number) => {
        criteria.page = page;
        getData();
    }

    useEffect(() => {
        loadFilters();
        setPageLoaded(true);
    }, []);

    useEffect(() => {
        if (pageLoaded) {
            getData();
        }
    }, [criteria]);

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem('VISITSREPORT');
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }
    }

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        criteria.registrationNumber = f.registrationNumber;
        criteria.stoppingAreaId = f.stoppingAreaId;
        criteria.entryDate = f.entryDate;
        criteria.exitDate = f.exitDate;
  
        if (isOnLoad && f.page) {
            setCurrentPage(f.page);
            criteria.page = f.page
        } else {
            setCurrentPage(1);
            criteria.page = 1;
            f.page = 1;
        }

        saveFiltersOnCache(f);
        getData();
    }

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.registrationNumber) {
            total++;
        }
        if (f.stoppingAreaId) {
            total++;
        }
        setFiltersTotal(total);
    }


    const renderCounterBoxes = () => {
        let hours = 0;
        let minutes = 0;
    
        if(counters){
            const hoursHelper = counters.minutes / 60
            hours = Math.floor(hoursHelper)
            minutes = hours > 0 ? (Math.fround((hoursHelper - hours)* 60)): counters.minutes  ;
        }

        return <div className={styles.rowCounters}>
            {counters && renderBox(HourglassIcon, t('visits_report.list.duration_in_hours'),undefined, `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} h`)}
            {counters && renderBox(PointIcon, t('visits_report.list.number_stopping_area'), counters.totalStoppingAreas)}
            {counters && renderBox(CarIcon, t('visits_report.list.number_vehicles'), counters.totalVehicles)}
            <ReactTooltip effect="solid" id="main" />
        </div>
    }

    const renderBox = (icon: any, label: string, counter?: number,counterString?:string, isMoney?: boolean, tooltip?: string) => {
        return <CounterBox
            icon={icon}
            label={label}
            counter={counter}
            counterString={counterString}
            isMoney={isMoney}
            data-tip={tooltip}
            data-for="main"
        />
    }



    return (
        <ScreenTitle title={t('visits_report.list.title')}>
            <ScreenContainer>
                <ScreenHeader title={t('visits_report.list.title')}>
                    <Popover
                        contentContainerClassName={styles.filtersPopoverContainer}
                        containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                        positions={['bottom', 'left']}
                        align={'end'}
                        onClickOutside={() => updateTotalFilters(filters)}
                        content={setIsPopoverOpen => <VisitsReportFiltersScreen
                            filters={filters}
                            onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f) }}
                            onChange={f => updateTotalFilters(f)}
                        />}>
                        {(isPopoverOpen, setIsPopoverOpen) => (
                            <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                {filtersTotal > 0 &&
                                    <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                }
                            </ScreenHeaderButton>
                        )}
                    </Popover>
                    <ReactTooltip />
                </ScreenHeader>

                <FilterList filters={[
                    {
                        value: filters.registrationNumber,
                        label: t('visits_report.list.registration'),
                        onRemove: () => { filters.registrationNumber = undefined; onChangeFilters(filters) }
                    },
                    {
                        value: filters.stoppingArea,
                        label: t('visits_report.list.stopping_area'),
                        onRemove: () => { filters.stoppingAreaId = filters.stoppingArea = undefined; onChangeFilters(filters) }
                    },
                    {
                        value: filters.entryDate && filters.exitDate ? <>{t('common.range.from')} <DateFormat value={filters.entryDate} /> {t('common.range.to').toLowerCase()} <DateFormat value={filters.exitDate} /></>
                            : filters.entryDate ? <>{t('common.range.from')} <DateFormat value={filters.entryDate} /></>
                                : filters.exitDate ? <>{t('common.range.to')} <DateFormat value={filters.exitDate} /></> : undefined,
                        label: t('visits_report.list.visit_date'),
                        onRemove: () => { filters.entryDate = filters.exitDate = undefined; onChangeFilters(filters) }
                    },

                ]} />
                {counters && renderCounterBoxes()}
                <Box>
                    <ListingTable
                        columns={tableColumns}
                        rows={stoppingAreaItems}
                        onRowClick={(row, _, event) => onRowClick(event, row.verificationId)}
                        onHref={row => row.verificationId && row.verificationId != guidEmpty ? `/verifications/details/${row.verificationId}` : ''}
                        allowHover={true}
                        initialSearch={{ colField: 'entry_date', isOrderAsc: false }}
                    />
                    <PaginationWithInfo
                        itemName={t('visits_report.list.title')}
                        currentPage={currentPage}
                        pageItems={stoppingAreaItems.length}
                        totalItems={totalItems}
                        onChange={onPageChange}
                    />
                </Box>

            </ScreenContainer>
        </ScreenTitle>
    );
}

export default VisitsReportScreen;
